import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

import { Neutrals, Primary } from '../../../core-components/theme/color-palette/colors';
import { settingsRowSx } from '../../../core-components/theme/components/SettingsList';
import { FOCUS_RING, FOCUS_RING_OFFSET } from '../../../core-components/theme/focusRing';

import { DURATION_FIELD_MAX, ROUTING_SEVERITY_COLUMN, ROUTING_TOLD_NOW_COLUMN } from './layout';

const COARSE = '@media (pointer: coarse)';
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

export const ADMIN_CONTROLS_MAX = '40rem';

export const durationFieldSx: SystemStyleObject<Theme> = { maxInlineSize: DURATION_FIELD_MAX };

export const collapseSx: SystemStyleObject<Theme> = { [REDUCED_MOTION]: { transition: 'none !important' } };

const ROUTING_TABLE_MIN = '44rem';

const WIDE = `@container (min-width: ${ROUTING_TABLE_MIN})`;

export const routingSectionSx: SystemStyleObject<Theme> = { display: 'block', inlineSize: '100%', containerType: 'inline-size' };
export const narrowOnlySx: SystemStyleObject<Theme> = { display: 'block', [WIDE]: { display: 'none' } };
export const wideOnlySx: SystemStyleObject<Theme> = { display: 'none', [WIDE]: { display: 'block' } };

export const severityItemSx: SystemStyleObject<Theme> = { ...settingsRowSx, display: 'flex', flexDirection: 'column', gap: 3 };
export const chipHeadingSx: SystemStyleObject<Theme> = { margin: 0, display: 'flex' };

export const routingTermsSx: SystemStyleObject<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'fit-content(45%) minmax(0, 1fr)',
  columnGap: 3,
  rowGap: 2,
  margin: 0,
  '& > dt': { color: Neutrals[600], overflowWrap: 'anywhere' },
  '& > dd': { margin: 0, minInlineSize: 0 },
};

export const routingTableCardSx: SystemStyleObject<Theme> = { padding: 0, overflow: 'clip' };

export const routingTableSx: SystemStyleObject<Theme> = {
  inlineSize: '100%',
  borderCollapse: 'collapse',
  '& th, & td': {
    textAlign: 'start',
    verticalAlign: 'top',
    paddingBlock: 3,
    paddingInline: 4,
    overflowWrap: 'anywhere',
  },
  '& thead th': { color: Neutrals[600], backgroundColor: Neutrals[50], paddingBlock: 2 },
  '& thead th:first-of-type, & tbody th': { inlineSize: ROUTING_SEVERITY_COLUMN },
  '& thead th:nth-of-type(2)': { inlineSize: ROUTING_TOLD_NOW_COLUMN },
  '& tbody tr': { borderBlockStart: `1px solid ${Neutrals[100]}` },
};

const semiBold = (theme: Theme): number => theme.typography.fontWeightSemiBold;

export const personLinkSx: SystemStyleObject<Theme> = {
  color: Primary[600],
  fontWeight: semiBold,
  textDecoration: 'none',
  overflowWrap: 'anywhere',
  '&:hover, &:focus-visible': { textDecoration: 'underline', textUnderlineOffset: (theme: Theme) => theme.typography.underlineOffset },
  '&:focus-visible': { outline: FOCUS_RING, outlineOffset: FOCUS_RING_OFFSET },
  [COARSE]: {
    display: 'inline-flex',
    alignItems: 'center',
    minBlockSize: (theme: Theme) => theme.controlHeight.medium,
    minInlineSize: (theme: Theme) => theme.controlHeight.medium,
  },
};

export const personRoleSx: SystemStyleObject<Theme> = { color: Neutrals[600] };

